/**
 * Service pour la gestion des contacts d'une organisation.
 *
 * Fournit des méthodes pour :
 * - createOrgContact    : créer un contact pour une organisation.
 * - searchOrgContacts   : lister les contacts d'une organisation.
 * - getOrgContactById   : récupérer un contact par son ID.
 * - updateOrgContact    : mettre à jour un contact.
 * - deleteOrgContact    : supprimer un contact.
 *
 * Utilise axiosInstance pour les requêtes HTTP avec interceptors configurés.
 */
import type { AxiosResponse } from 'axios';
import axiosInstance from '@/services/interceptor.services';
import type {
  IOrganisationListContact,
  ICreateOrgContactBody,
  IUpdateOrgContactBody,
} from '@/types/manageOrgCommerce.types';

export const OrgContactService = {
  /**
   * Crée un contact pour une organisation.
   * @param organizationId - UUID de l'organisation
   * @param data - Corps de la requête
   */
  createOrgContact: async (
    organizationId: string,
    data: ICreateOrgContactBody,
  ): Promise<
    AxiosResponse<{ data: IOrganisationListContact; message: string }>
  > => {
    try {
      const response = await axiosInstance.post<{
        data: IOrganisationListContact;
        message: string;
      }>(`organizations/${organizationId}/contacts/create`, data, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  },

  /**
   * Liste les contacts d'une organisation.
   * @param organizationId - UUID de l'organisation
   */
  searchOrgContacts: async (
    organizationId: string,
  ): Promise<AxiosResponse<IOrganisationListContact[]>> => {
    try {
      const response = await axiosInstance.get<IOrganisationListContact[]>(
        `organizations/${organizationId}/contacts/search`,
      );
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  },

  /**
   * Récupère un contact par son ID.
   * @param organizationId - UUID de l'organisation
   * @param contactId - UUID du contact
   */
  getOrgContactById: async (
    organizationId: string,
    contactId: string,
  ): Promise<AxiosResponse<IOrganisationListContact>> => {
    try {
      const response = await axiosInstance.get<IOrganisationListContact>(
        `organizations/${organizationId}/contacts/get/${contactId}`,
      );
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  },

  /**
   * Met à jour un contact d'organisation.
   * @param organizationId - UUID de l'organisation
   * @param contactId - UUID du contact
   * @param data - Corps de la requête
   */
  updateOrgContact: async (
    organizationId: string,
    contactId: string,
    data: IUpdateOrgContactBody,
  ): Promise<
    AxiosResponse<{ data: IOrganisationListContact; message: string }>
  > => {
    try {
      const response = await axiosInstance.put<{
        data: IOrganisationListContact;
        message: string;
      }>(`organizations/${organizationId}/contacts/update/${contactId}`, data, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  },

  /**
   * Supprime un contact d'organisation.
   * @param organizationId - UUID de l'organisation
   * @param contactId - UUID du contact
   */
  deleteOrgContact: async (
    organizationId: string,
    contactId: string,
  ): Promise<AxiosResponse<{ message: string }>> => {
    try {
      const response = await axiosInstance.delete<{ message: string }>(
        `organizations/${organizationId}/contacts/delete/${contactId}`,
      );
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  },
};
